import { CLUSTER_PRIMARY } from "./cluster-map.generated";

/**
 * Konsolidering av söktintention → en primär URL.
 *
 * Underlag: Google Search Console, property https://fenny.se/,
 * perioden 2026-08-06 – 2026-09-02 (dimensioner page + query).
 * Flera sidor visades på samma fråga och delade upp signalerna, vilket
 * höll ner positionen för allihop. Den sida som redan rankade bäst på
 * frågan blir primär; övriga pekar canonical dit.
 *
 * Nyckel = sökväg utan domän och utan avslutande slash.
 * Värde = primär sökväg.
 */
export const CANONICAL_OVERRIDES: Record<string, string> = {
  // Intention: "företagsfilm pris" / "vad kostar en företagsfilm"
  // Primär: /kunskap/vad-kostar-en-foretagsfilm (pos 6,2 · 105 visningar)
  "/vad-kostar-en-foretagsfilm": "/kunskap/vad-kostar-en-foretagsfilm",
  "/fragor/vad-kostar-foretagsfilm": "/kunskap/vad-kostar-en-foretagsfilm",

  // Intention: "företagsfilm" / "företagsfilmer" / "produktion företagsfilm"
  // Primär: /kunskap/vad-ar-en-foretagsfilm (pos 15,4 · 535 visningar)
  "/fragor/vad-ar-foretagsfilm": "/kunskap/vad-ar-en-foretagsfilm",

  // Intention: "skillnad reklamfilm företagsfilm"
  // Primär: /kunskap/skillnad-reklamfilm-foretagsfilm
  "/fragor/skillnaden-foretagsfilm-och-reklamfilm":
    "/kunskap/skillnad-reklamfilm-foretagsfilm",

  // Intention: "reklamfilm" / "reklam film" / "reklamfilm produktion"
  // Primär: /reklamfilm (tjänstesidan, pos 16,6 och konverterar). GSC okt 2026:
  // frågesidan (245 visn, pos 23,8) och kunskapsartikeln (134 visn, pos 46,3)
  // delade signalerna utan att ta klick.
  "/fragor/vad-ar-reklamfilm": "/reklamfilm",
  "/kunskap/vad-ar-en-reklamfilm": "/reklamfilm",

  // Intention: "vad kostar reklamfilm"
  // Primär: /vad-kostar-en-reklamfilm
  "/fragor/vad-kostar-reklamfilm": "/vad-kostar-en-reklamfilm",

  // Intention: "drönarfilm" / "drönarfilmsproduktion"
  // Primär: /dronarfilm (tjänstesidan). Kunskapsartikeln om vad drönarfilm är
  // delade signalerna utan att ranka (pos 23–42). Artikeln om regler och
  // tillstånd behåller sin egen intention och står kvar som primär.
  "/kunskap/vad-ar-dronarfilm": "/dronarfilm",

  // Intention: "företagsfilm halmstad" / "reklamfilm halmstad"
  // Primär: /foretagsfilm/halmstad (pos 6,4). Filmbyråsidan för samma ort
  // delade signalerna på pos 13–16 utan att ta klick. GSC okt 2026: även
  // reklamfilmssidan för Halmstad delade signalerna på samma fråga.
  "/filmbyra/halmstad": "/foretagsfilm/halmstad",
  "/reklamfilm/halmstad": "/foretagsfilm/halmstad",

  // Intention: "företagsfilm malmö". GSC okt 2026: nio egna sidor delade
  // signalerna. Primär: /foretagsfilm/malmo (egen intention, står kvar via
  // KEEP i klusterkartan); övriga Malmö-sidor konsolideras av klusterkartan
  // mot /filmproduktion/malmo respektive pekar hit enligt nedan.
  "/videobyra/malmo": "/foretagsfilm/malmo",

  // Intention: "b2b video" / "film för b2b". Två sidor delade identisk title
  // och samma intention. Primär: /b2b-video (tjänstesidan med bredare innehåll).
  "/branscher/video-for-b2b": "/b2b-video",

  // Identisk H1/intention: rot-URL:en dubblerar frågesidan.
  "/hur-lang-ska-en-reklamfilm-vara": "/fragor/hur-lang-reklamfilm",

  // Intention: "produktionsbolag film" / "filmproduktionsbolag" / "film produktioner".
  // Primär: /filmproduktionsbolag (djupare köpguide). Semrush okt 2026.
  "/produktionsbolag-film": "/filmproduktionsbolag",

  // Intention: "explainer" / "explainer film" / "animerad explainer".
  // Primär: /explainer-video (pos 5,4). GSC okt 2026: kunskapsartikeln delade
  // signalerna på samma frågor (ca 130 visn, pos 8,7–26) utan klick.
  "/kunskap/vad-ar-en-explainer-video": "/explainer-video",

  // Intention: "produktfilm". Primär: /produktfilm (pos 7,6). GSC okt 2026:
  // branschstudien delade signalerna på pos 57 utan klick.
  "/branschstudier/produktfilm": "/produktfilm",

  // Engelska speglingar: /en/* är den kanoniska engelska strukturen.
  "/english/film-production-sweden": "/en/film-production-sweden",
  "/english/video-agency-stockholm": "/en/video-agency-stockholm",
};

/**
 * Klusterkonsolidering (genererad): stadssidor pekar mot /filmproduktion/{stad},
 * prisguider mot motsvarande "vad kostar"-fråga. Manuella overrides ovan vinner.
 */
function normalize(path: string): string {
  const p = path.split("?")[0]!.split("#")[0]!;
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

/**
 * Sidor som manuella regler (GSC-data) pekar ut som primära. De får aldrig
 * konsolideras vidare av den genererade klusterkartan.
 */
const MANUAL_PRIMARIES = new Set(Object.values(CANONICAL_OVERRIDES));

function mappedTarget(path: string): string | undefined {
  const manual = CANONICAL_OVERRIDES[path];
  if (manual) return manual;
  if (MANUAL_PRIMARIES.has(path)) return undefined;
  return CLUSTER_PRIMARY[path];
}

/** Returnerar den primära sökvägen för en sida (sig själv om ingen override finns). */
export function primaryPathFor(path: string): string {
  const normalized = normalize(path);
  const direct = mappedTarget(normalized);
  if (!direct || direct === normalized) return normalized;
  // Undvik kedjor: följ ett extra steg och stanna där.
  const next = mappedTarget(direct);
  return next && next !== direct ? next : direct;
}

/** Absolut canonical-URL för en sökväg, med konsolidering inräknad. */
export function canonicalUrlFor(path: string): string {
  return `https://fenny.se${primaryPathFor(path)}`;
}

/** True när sidan är en stödsida som pekar canonical mot en annan URL. */
export function isConsolidated(path: string): boolean {
  return primaryPathFor(path) !== (path.length > 1 ? path.replace(/\/+$/, "") : path);
}
